"use client";
import { useState, useEffect, useRef, useCallback } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { Formik } from "formik";
import * as Yup from "yup";
import { addMcq, getMcqCourseWiseStats } from "../../../../services/McqService.js";
import { getcourses } from "../../../../services/CourseModule";
import { getMe } from "../../../../services/AuthService";
import { getTrainers, getTrainerByUserId } from "../../../../services/TrainerService";
import NewCustomAsyncSelectDropdown from "../../common/NewCustomAsyncSelectDropdown.jsx";
import { toast } from '../../common/Toast/toast';
import PopupDone from '../../common/PopupDone/PopupDone';
import debounce from "lodash/debounce";
import McqList from "./McqList.jsx";
import { mcqStepComplete } from "./mcqSteps.js";
import "../TrainerDashboard.css";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useScrollLoadMore from "../../common/useScrollLoadMore";

// The add form asks three things in turn: the question, the answers,
// and where it belongs.
const MCQ_STEPS = ["Question", "Answers", "Courses"];
const MCQ_STEP_COPY = [
  { title: "What is the question?", hint: "Write the question as students will read it." },
  { title: "What are the options?", hint: "Four options, and which of them is right." },
  { title: "Where does it belong?", hint: "Pick the courses this question is for." },
];

const DisplayMcqCourse = () => {
  // Which of the three the add dialog is on.
  // The trainer filter's own option list. The attendance filters drive
  // NewCustomAsyncSelectDropdown this way -- options plus callbacks --
  // rather than handing it a loader.
  const [filterTrainerOptions, setFilterTrainerOptions] = useState([]);
  const [filterTrainerPage, setFilterTrainerPage] = useState(1);
  const [filterTrainerHasMore, setFilterTrainerHasMore] = useState(true);
  const [filterTrainerLoading, setFilterTrainerLoading] = useState(false);
  const [filterTrainerSearch, setFilterTrainerSearch] = useState("");
  const filterTrainerSeq = useRef(0);

  const fetchFilterTrainers = useCallback(
    async (page = 1, search = "", append = false) => {
      const reqId = ++filterTrainerSeq.current;
      setFilterTrainerLoading(true);
      try {
        const { options, hasNext } = await loadTrainerOptionsRef.current({
          search,
          page,
          limit: 20,
        });
        // A newer request has started; drop this one rather than let it
        // overwrite fresher options.
        if (reqId !== filterTrainerSeq.current) return;
        setFilterTrainerOptions((prev) =>
          append ? [...prev, ...options] : options,
        );
        setFilterTrainerHasMore(Boolean(hasNext));
        setFilterTrainerPage(page);
      } catch {
        if (reqId === filterTrainerSeq.current) {
          if (!append) setFilterTrainerOptions([]);
          setFilterTrainerHasMore(false);
        }
      } finally {
        if (reqId === filterTrainerSeq.current) setFilterTrainerLoading(false);
      }
    },
    [],
  );

  const [mcqStep, setMcqStep] = useState(1);
  const [mcqShownStep, setMcqShownStep] = useState(1);
  const [mcqPhase, setMcqPhase] = useState("in");

  useEffect(() => {
    if (mcqShownStep === mcqStep) return undefined;
    setMcqPhase("out");
    const t = setTimeout(() => {
      setMcqShownStep(mcqStep);
      setMcqPhase("in");
    }, 160);
    return () => clearTimeout(t);
  }, [mcqStep, mcqShownStep]);

  const [courses, setCourses] = useState([]);
  const [coursesTotal, setCoursesTotal] = useState(0);
  const [totalMcqs, setTotalMcqs] = useState(0);
  const [coursesTotalPages, setCoursesTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [coursesPerPage, setCoursesPerPage] = useState(25);
  // Whether a further page is actually in flight. The ref guards against
  // double-firing; this is what the foot reads, so it can say "loading"
  // only when something is.
  const [loadingMore, setLoadingMore] = useState(false);
  // What the add dialog says after a submit -- kept inside the dialog
  // rather than thrown to a toast in the corner.
  const [formNotice, setFormNotice] = useState(null);
  // Page 1 replaces the list; every page after it appends. A ref because
  // the fetch reads it after an await, when a re-render may have moved
  // on already.
  const appendRef = useRef(false);
  const loadingMoreRef = useRef(false);
  const listEndRef = useRef(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [trainerId, setTrainerId] = useState("");
  const [actualTrainerId, setActualTrainerId] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [isTrainer, setIsTrainer] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTrainerFilter, setSelectedTrainerFilter] = useState("");
  const [selectedTrainerFilterOption, setSelectedTrainerFilterOption] = useState({
    label: "All Trainers",
    value: "",
  });
  const [selectedTrainerObjectId, setSelectedTrainerObjectId] = useState(""); // Store selected trainer's object ID
  const [trainerIdMapping, setTrainerIdMapping] = useState({}); // Map userId to trainer object ID
  // The fetch reads the mapping, but a new mapping is not a reason to
  // fetch again. Opening the trainer filter loads trainer options, which
  // updated this state, which re-ran the course fetch -- so simply
  // opening the menu reloaded the list, once per page of trainers.
  const trainerIdMappingRef = useRef({});
  useEffect(() => {
    trainerIdMappingRef.current = trainerIdMapping;
  }, [trainerIdMapping]);
  const [initialized, setInitialized] = useState(false);
  const lastStatsReqRef = useRef(0);

  const initializeComponent = async () => {
    try {
      setLoading(true);
      const userProfile = await getMe();
      const userData = userProfile.data;
      const userId = userData.id || userData._id || userData.userId;

      if (!userId) {
        toast.error("Unable to fetch user information");
        return;
      }

      setCurrentUser(userData);

      const profileLabel = userData.profiles?.[0]?.label?.toLowerCase();
      const isTrainerUser = profileLabel === "trainer";
      const isAdminUser = profileLabel === "admin" || userData.isSuperAdmin;

      setIsTrainer(isTrainerUser);
      setIsAdmin(isAdminUser);

      if (isTrainerUser) {
        try {
          const trainerResponse = await getTrainerByUserId(userId);
          const trainerData = trainerResponse.data;
          const actualTrainerObjectId = trainerData.id || trainerData._id;
          
          if (actualTrainerObjectId) {
            setActualTrainerId(actualTrainerObjectId);
            setTrainerId(actualTrainerObjectId);
          } else {
            setActualTrainerId(userId);
            setTrainerId(userId);
          }
        } catch (err) {
          console.error("Error fetching trainer data:", err);
          setActualTrainerId(userId);
          setTrainerId(userId);
        }
      }

      setSelectedTrainerFilter("");
      setSelectedTrainerFilterOption({ label: "All Trainers", value: "" });
      setSelectedTrainerObjectId("");
      setInitialized(true);
      setError(null);
    } catch (err) {
      console.error("Error initializing component:", err);
      toast.error("Failed to initialize component");
      setError("Failed to initialize component");
    } finally {
      setLoading(false);
    }
  };

  // Fetch trainer object ID by userId
  const fetchTrainerObjectId = async (userId) => {
    if (!userId) return null;
    
    // Check if we already have the mapping
    if (trainerIdMapping[userId]) {
      return trainerIdMapping[userId];
    }
    
    try {
      const response = await getTrainerByUserId(userId);
      const trainerData = response.data;
      const trainerObjectId = trainerData.id || trainerData._id;
      
      if (trainerObjectId) {
        setTrainerIdMapping(prev => ({
          ...prev,
          [userId]: trainerObjectId
        }));
        return trainerObjectId;
      }
      return userId;
    } catch (err) {
      console.error("Error fetching trainer object ID:", err);
      return userId;
    }
  };

  const toTrainerOption = (trainer) => {
    if (!trainer) return null;
    const id = String(trainer.userId || trainer.trainerId || trainer.id || "").trim();
    if (!id) return null;
    const name = trainer.trainerName || "";
    const email = trainer.trainerEmail || "";
    const label = email ? `${name} (${email})` : name || id;
    return { value: id, label, trainerObjectId: trainer.id || trainer._id };
  };

  // The form's own pickers, driven the way the attendance filters drive
  // theirs: options plus callbacks, not a loader.
  const [formCourseOptions, setFormCourseOptions] = useState([]);
  const [formCoursePage, setFormCoursePage] = useState(1);
  const [formCourseHasMore, setFormCourseHasMore] = useState(true);
  const [formCourseLoading, setFormCourseLoading] = useState(false);
  const [formCourseSearch, setFormCourseSearch] = useState("");
  const formCourseSeq = useRef(0);
  const loadCourseOptionsRef = useRef(null);

  const fetchFormCourses = useCallback(async (page = 1, search = "", append = false) => {
    const reqId = ++formCourseSeq.current;
    setFormCourseLoading(true);
    try {
      const { options, hasNext } = await loadCourseOptionsRef.current({
        search,
        page,
        limit: 20,
      });
      if (reqId !== formCourseSeq.current) return;
      setFormCourseOptions((prev) => (append ? [...prev, ...options] : options));
      setFormCourseHasMore(Boolean(hasNext));
      setFormCoursePage(page);
    } catch {
      if (reqId === formCourseSeq.current && !append) setFormCourseOptions([]);
    } finally {
      if (reqId === formCourseSeq.current) setFormCourseLoading(false);
    }
  }, []);

  const loadTrainerOptionsRef = useRef(null);
  const loadTrainerOptions = async ({ search, page, limit }) => {
    const resp = await getTrainers(limit, page, search || "", false);
    const trainers = Array.isArray(resp?.data) ? resp.data : [];
    
    // Build mapping of userId to trainer object ID
    const mapping = {};
    trainers.forEach(trainer => {
      const userId = trainer.userId || trainer.trainerId || trainer.id;
      const trainerObjId = trainer.id || trainer._id;
      if (userId && trainerObjId) {
        mapping[userId] = trainerObjId;
      }
    });
    setTrainerIdMapping(prev => ({ ...prev, ...mapping }));
    
    const next = trainers.map(toTrainerOption).filter(Boolean);
    const options =
      page === 1 && !String(search || "").trim()
        ? [{ label: "All Trainers", value: "", trainerObjectId: "" }, ...next]
        : next;
    return { options, hasNext: Boolean(resp?.pagination?.hasNext) };
  };
  loadTrainerOptionsRef.current = loadTrainerOptions;

  const toCourseOption = (course) => {
    if (!course) return null;
    const id = String(course.id || course._id || "").trim();
    if (!id) return null;
    return { value: id, label: course.name || id };
  };

  const loadCourseOptions = async ({ search, page, limit }) => {
    const resp = await getcourses(limit, page, search || "", { includeTotal: false, searchField: "name" });
    const next = (Array.isArray(resp?.data) ? resp.data : []).map(toCourseOption).filter(Boolean);
    return { options: next, hasNext: Boolean(resp?.pagination?.hasNext) };
  };
  loadCourseOptionsRef.current = loadCourseOptions;

  const fetchCourseWiseStats = async () => {
    const seq = (lastStatsReqRef.current += 1);
    const isAppend = appendRef.current;
    // Held for the life of the request, the way the reference list's
    // isFetching is: loadMore reads it and never sets it, so a page that
    // never lands cannot strand it.
    loadingMoreRef.current = true;
    try {
      // Captured before the request, and conditional: the full-page
      // loader must never appear for a scroll batch. It was raised for
      // every fetch, so each appended page unmounted the whole list and
      // drew "Loading Courses..." in its place -- the flicker. Only a
      // fresh load -- a filter, a search, the first paint -- blocks now.
      if (isAppend) setLoadingMore(true);
      else setLoading(true);
      
      // Determine which trainer ID to use - always use the actual trainer object ID
      let filterTrainerId = "";
      if (isTrainer) {
        // For trainer logged in, use the actual trainer object ID
        filterTrainerId = actualTrainerId;
      } else if (isAdmin && selectedTrainerFilter) {
        // For admin with selected trainer filter, get the actual trainer object ID
        const selectedUserId = selectedTrainerFilter;
        // Check if we have the mapping
        if (trainerIdMappingRef.current[selectedUserId]) {
          filterTrainerId = trainerIdMappingRef.current[selectedUserId];
        } else {
          // Fetch the trainer object ID
          filterTrainerId = await fetchTrainerObjectId(selectedUserId);
        }
      }
      
      const res = await getMcqCourseWiseStats({
        trainerId: filterTrainerId || undefined,
        search: searchTerm,
        page: currentPage,
        limit: coursesPerPage,
        includeTotal: true,
      });
      if (seq !== lastStatsReqRef.current) return;
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      const mapped = items.map((it) => ({
        id: String(it.courseId || "").trim(),
        name: it.courseName || "",
        code: it.courseCode || "",
        description: it.courseDescription || "",
        mcqCount: Number(it.mcqCount) || 0,
        trainerNames: Array.isArray(it.trainerNames) ? it.trainerNames : [],
      })).filter((x) => x.id);

      // isAppend, captured before the request went out -- not the live
      // ref. Anything that resets the ref while a page is in flight (the
      // filter effect, a search, the trainer picker) turned the append
      // into a replace, and the list snapped back to its first page.
      // That was the glitch on load-more.
      if (isAppend) {
        setCourses((prev) => {
          const seen = new Set(prev.map((c) => c.id));
          return [...prev, ...mapped.filter((c) => !seen.has(c.id))];
        });
      } else {
        setCourses(mapped);
      }
      appendRef.current = false;
      setCoursesTotal(Number(res?.pagination?.total) || mapped.length);
      setCoursesTotalPages(Number(res?.pagination?.totalPages) || 1);
      setTotalMcqs(Number(res?.data?.summary?.totalMcqs) || 0);
      setError(null);
    } catch (err) {
      console.error("Error fetching course-wise stats:", err);
      if (seq !== lastStatsReqRef.current) return;
      if (!appendRef.current) setCourses([]);
      appendRef.current = false;
      setCoursesTotal(0);
      setCoursesTotalPages(1);
      setTotalMcqs(0);
      setError("Failed to load course-wise stats");
    } finally {
      if (seq === lastStatsReqRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
      loadingMoreRef.current = false;
    }
  };

  useEffect(() => {
    initializeComponent();
  }, []);

  // Two effects, not one. They were the same debounced effect, so a
  // scroll-triggered page change waited 350ms behind whatever else had
  // touched the filters -- and a filter change arriving mid-wait reset
  // the timer while appendRef was still set, appending a page that
  // should have replaced the list. That is what glitched on load-more.

  // Filters: debounced, and always a fresh list from the top.
  useEffect(() => {
    if (!initialized) return undefined;
    if (!isAdmin && !isTrainer) return undefined;
    const t = setTimeout(() => {
      appendRef.current = false;
      loadingMoreRef.current = false;
      if (currentPage !== 1) {
        setCurrentPage(1);      // the page effect below does the fetch
      } else {
        fetchCourseWiseStats();
      }
    }, 350);
    return () => clearTimeout(t);
    // trainerIdMapping is deliberately absent -- see the ref above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialized,
    isAdmin,
    isTrainer,
    actualTrainerId,
    selectedTrainerFilter,
    searchTerm,
    coursesPerPage,
  ]);

  // Pages: immediate, and never debounced -- the scroll already decided.
  //
  // Page 1 belongs to the filter effect above, so this only ever handles
  // 2 and beyond. It used to skip its own first run with a ref instead,
  // which was wrong: on mount `initialized` is still false, so the
  // effect returned before consuming that ref -- and the ref was then
  // spent on the first real page change, swallowing it. The scroll set
  // loadingMoreRef, no fetch followed to release it, and nothing loaded
  // again after that.
  useEffect(() => {
    if (!initialized) return;
    if (!isAdmin && !isTrainer) return;
    if (currentPage <= 1) return;
    fetchCourseWiseStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, initialized, isAdmin, isTrainer]);

  const handleCheckMCQ = (courseIds, courseName) => {
    setSelectedCourse({ id: courseIds, name: courseName });
  };

  const handleBackToCourses = () => {
    setSelectedCourse(null);
  };

  const handleTrainerFilterChange = async (selectedOption) => {
    const opt = selectedOption || { label: "All Trainers", value: "" };
    setSelectedTrainerFilterOption(opt);
    setSelectedTrainerFilter(opt.value || "");
    
    // If a trainer is selected, get their actual object ID
    if (opt.value) {
      const trainerObjectId = await fetchTrainerObjectId(opt.value);
      setSelectedTrainerObjectId(trainerObjectId);
    } else {
      setSelectedTrainerObjectId("");
    }
    appendRef.current = false;
    setCurrentPage(1);
  };
  
  const currentCourses = courses;
  const totalPages = Math.max(1, coursesTotalPages || 1);
  const hasMoreCourses = courses.length < coursesTotal;

  // The scroll asked for the next batch. Only ever moves forward, and
  // only while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || loading || loadingMore) return;
    if (courses.length >= coursesTotal) return;
    appendRef.current = true;
    // Derived from the rows already on screen rather than counted up, so
    // the batch size can change mid-list without asking for a page that
    // has already been read. 30 loaded at a size of 15 is page 3, not
    // page 2. Any overlap a ragged division causes is dropped by the id
    // guard where the rows are appended.
    const nextPage = Math.floor(courses.length / coursesPerPage) + 1;
    setCurrentPage(nextPage);
  }, [loading, loadingMore, courses.length, coursesTotal, coursesPerPage]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore. Reconnecting
  // on loadMore's identity is what re-fires it when the sentinel is still
  // in view after a short batch lands.
  useScrollLoadMore(listEndRef, loadMore, [loadMore]);

  const mcqValidationSchema = Yup.object().shape({
    question: Yup.string()
      .required("Question is required")
      .min(10, "Question must be at least 10 characters")
      .max(500, "Question cannot exceed 500 characters"),
    option1: Yup.string().required("Option 1 is required"),
    option2: Yup.string().required("Option 2 is required"),
    option3: Yup.string().required("Option 3 is required"),
    option4: Yup.string().required("Option 4 is required"),
    correctAnswer: Yup.number()
    .required("Correct answer is required")
    .oneOf([1, 2, 3, 4], "Please select a valid option (1-4)"),
    selectedCourses: Yup.array()
      .min(1, "Please select at least one course")
      .required("Course selection is required"),
    selectedTrainer: isAdmin 
    ? Yup.object()
        .nullable()
        .required("Please select a trainer")
    : Yup.mixed().nullable(),
  });

  const mcqInitialValues = {
    question: "",
    option1: "",
    option2: "",
    option3: "",
    option4: "",
    correctAnswer: "",
    selectedCourses: [],
    selectedTrainer: null,
  };

  const getCurrentTrainerName = () => {
    if (!currentUser) return "";
    const firstName = currentUser.firstName || "";
    const lastName = currentUser.lastName || "";
    const email = currentUser.email || "";

    if (firstName || lastName) {
      let name = `${firstName} ${lastName}`.trim();
      if (email) {
        name += ` (${email})`;
      }
      return name;
    }
    return email;
  };

  const handleMcqSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setFormSubmitting(true)
      
      let trainerIdToUse = ""
      if (isTrainer) {
        trainerIdToUse = actualTrainerId
      } else if (isAdmin) {
        // For admin, get the actual trainer object ID from the selected trainer
        const selectedTrainerValue = values.selectedTrainer?.value || ""
        if (selectedTrainerValue) {
          trainerIdToUse = await fetchTrainerObjectId(selectedTrainerValue)
        }
      }
      
      if (!trainerIdToUse) {
        toast.error("Trainer ID is required")
        return
      }
      
      const courseIds = values.selectedCourses.map(course => course.value).join(",")
      
      const correctAnswerNumber = parseInt(values.correctAnswer, 10);
    
    const mcqData = {
      question: values.question,
      option1: values.option1,
      option2: values.option2,
      option3: values.option3,
      option4: values.option4,
      correctAnswer: correctAnswerNumber,
      courseIds: courseIds,
      trainerId: trainerIdToUse,
    }
      
      await addMcq(mcqData)

      // Said here rather than in a toast: the reader is looking at this
      // dialog, and a corner notification about the thing in front of
      // them is easy to miss.
      const n = values.selectedCourses.length
      setFormNotice({
        kind: "ok",
        text: `Added to ${n} course${n === 1 ? "" : "s"}.`,
      })
      resetForm()
      setMcqStep(1)
      setMcqShownStep(1)
      appendRef.current = false
      setCurrentPage(1)
      await fetchCourseWiseStats()
    } catch (error) {
      console.error("Error adding MCQ:", error)
      setFormNotice({ kind: "bad", text: "Could not add the MCQ. Try again." })
    } finally {
      setFormSubmitting(false)
      setSubmitting(false)
    }
  }

  if (error) {
    return (
      <div>
        <div>
          <div>
            <svg
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="1.6"
  strokeLinecap="round"
  strokeLinejoin="round"
  width="28"
  height="28"
><circle
  cx="12"
  cy="12"
  r="10"
/><line
  x1="12"
  y1="8"
  x2="12"
  y2="12"
/><line
  x1="12"
  y1="16"
  x2="12.01"
  y2="16"
/></svg>
          </div>
          <h3>Connection Error</h3>
          <p>{error}</p>
          <button type="button" onClick={initializeComponent}>
            <svg
  viewBox="0 0 24 24"
  fill="none"
  stroke="currentColor"
  strokeWidth="2"
  strokeLinecap="round"
  strokeLinejoin="round"
  width="14"
  height="14"
><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 102.13-9.36L1 10"/></svg>
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="pg">

      <header className="pg-head pg-head--bare">
        <div className="pg-head-actions">
          <button
            type="button"
            className="pg-btn pg-btn--main"
            onClick={() => {
              setMcqStep(1);
              setMcqShownStep(1);
              setFormNotice(null);
              setShowAddForm(true);
            }}
          >
            <Plus />
            Add MCQ
          </button>
        </div>
      </header>

      {/* Label above the figure, tone as a rule on the top edge -- the
          same stat treatment the dashboard uses. */}
      <div className="mcq-stats">
        <div className="att-stat">
          <span className="att-stat-label">
            {(isAdmin && selectedTrainerFilter) || isTrainer
              ? "Trainer's courses"
              : "Courses"}
          </span>
          <b className="att-stat-value">{coursesTotal}</b>
        </div>
        <div className="att-stat att-stat--good">
          <span className="att-stat-label">
            {(isAdmin && selectedTrainerFilter) || isTrainer
              ? "Trainer's MCQs"
              : "Valid MCQs"}
          </span>
          <b className="att-stat-value">{totalMcqs}</b>
        </div>
      </div>

      {/* Everything that narrows the list, on one line. */}
      <FilterSheet label="Filter courses">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="mcq-search">
              Search
            </label>
            <input
              id="mcq-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search courses by name, description or code..."
              value={searchTerm}
              onChange={(e) => {
                appendRef.current = false;
                setCurrentPage(1);
                setSearchTerm(e.target.value);
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="mcq-rows">
              Rows per load
            </label>
            <select
              id="mcq-rows"
              className="pg-select"
              value={coursesPerPage}
              onChange={(e) => {
                // Only the size of the next batch. The filter effect
                // takes it from the top so the list stays consistent.
                appendRef.current = false;
                setCoursesPerPage(Number(e.target.value));
              }}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          {isAdmin && (
            <div className="pg-field pg-field--grow">
              <label className="pg-label">Trainer</label>
              <NewCustomAsyncSelectDropdown
                options={filterTrainerOptions}
                value={
                  selectedTrainerFilterOption &&
                  selectedTrainerFilterOption.value
                    ? [selectedTrainerFilterOption]
                    : []
                }
                onChange={(opts) =>
                  handleTrainerFilterChange(
                    Array.isArray(opts) && opts.length
                      ? opts[opts.length - 1]
                      : null,
                  )
                }
                placeholder="All trainers"
                isMulti={false}
                isLoading={filterTrainerLoading}
                searchInControl={true}
                onMenuOpen={async () => {
                  setFilterTrainerPage(1);
                  setFilterTrainerSearch("");
                  await fetchFilterTrainers(1, "", false);
                }}
                onInputChange={debounce(async (value) => {
                  setFilterTrainerSearch(value);
                  setFilterTrainerPage(1);
                  await fetchFilterTrainers(1, value, false);
                }, 400)}
                onMenuScrollToBottom={async () => {
                  if (filterTrainerHasMore && !filterTrainerLoading) {
                    await fetchFilterTrainers(
                      filterTrainerPage + 1,
                      filterTrainerSearch,
                      true,
                    );
                  }
                }}
                onRefresh={async () => {
                  setFilterTrainerPage(1);
                  setFilterTrainerSearch("");
                  await fetchFilterTrainers(1, "", false);
                }}
              />
            </div>
          )}
        </div>
      </FilterSheet>

      {error ? (
        <div className="pg-state">
          <p className="pg-state-title">Could not load courses</p>
          <p className="pg-state-text">{error}</p>
          <button
            type="button"
            className="pg-btn"
            onClick={initializeComponent}
          >
            Try again
          </button>
        </div>
      ) : loading && currentCourses.length === 0 ? (
        <div className="pg-state">
          <span className="pg-spinner" aria-hidden="true" />
          <p className="pg-state-text">Loading courses...</p>
        </div>
      ) : currentCourses.length === 0 ? (
        <div className="pg-state">
          <p className="pg-state-title">No courses found</p>
          <p className="pg-state-text">
            {(isAdmin && selectedTrainerFilter) || isTrainer
              ? "This trainer has no courses with MCQs."
              : "No course matches that search."}
          </p>
          <button
            type="button"
            className="pg-btn"
            onClick={() => {
              setSearchTerm("");
              setSelectedTrainerFilter("");
              setSelectedTrainerFilterOption({
                label: "All Trainers",
                value: "",
              });
              setSelectedTrainerObjectId("");
              appendRef.current = false;
              setCurrentPage(1);
            }}
          >
            Clear filters
          </button>
        </div>
      ) : (
        <>
          {/* One row per course, the whole row opens it -- the same
              shape as the attendance list. */}
          <ul className="att-picklist-rows mcq-list">
            {currentCourses.map((course) => (
              <li key={course.id}>
                <button
                  type="button"
                  className="att-pick mcq-row"
                  onClick={() => handleCheckMCQ(course.id, course.name)}
                >
                  <span className="att-pick-id">
                    <b>{course.name}</b>
                    <span>
                      {[
                        course.code,
                        course.trainerNames && course.trainerNames.length
                          ? course.trainerNames.join(", ")
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ") || "No trainer yet"}
                    </span>
                  </span>

                  <span className="mcq-count">
                    <b>{course.mcqCount || 0}</b>
                    <span>MCQs</span>
                  </span>

                  <ChevronRight className="mcq-row-caret" />
                </button>
              </li>
            ))}
          </ul>

          {/* Fixed height whether loading or finished, so a page
              arriving never shunts the rows above it. */}
          <div className="att-picklist-foot" ref={listEndRef}>
            {loadingMore ? (
              <>
                <span className="pg-spinner att-spinner" aria-hidden="true" />
                <span>Loading more courses...</span>
              </>
            ) : hasMoreCourses ? (
              /* More to come, but nothing in flight -- say where the
                 reader is rather than claim to be loading. */
              <span>
                {currentCourses.length} of {coursesTotal} courses
              </span>
            ) : (
              <span>
                All {coursesTotal} course{coursesTotal === 1 ? "" : "s"} loaded
              </span>
            )}
          </div>
        </>
      )}

      {/* A course opens over the list rather than replacing the page, so
          closing it puts the reader back where they were. */}
      {selectedCourse && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mcq-course-title"
          onClick={handleBackToCourses}
        >
          <div
            className="pg-modal-panel pg-modal-panel--xl"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">MCQs</span>
                <h3 className="pg-modal-title" id="mcq-course-title">
                  {selectedCourse.name}
                </h3>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={handleBackToCourses}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              <McqList
                embedded
                courseId={selectedCourse.id}
                courseName={selectedCourse.name}
                onBack={handleBackToCourses}
                selectedTrainerFilter={
                  isTrainer ? actualTrainerId : selectedTrainerObjectId
                }
              />
            </div>
          </div>
        </div>
      )}

      {/* Adding an MCQ happens in a dialog rather than by pushing the
          list down the page. */}
      {showAddForm && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mcq-add-title"
          onClick={() => setShowAddForm(false)}
        >
          <div
            className="pg-modal-panel pg-modal-panel--wide pg-modal-panel--steady"
            onClick={(e) => e.stopPropagation()}
          >
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Add MCQ</span>
                <h3 className="pg-modal-title" id="mcq-add-title">
                  {MCQ_STEP_COPY[mcqStep - 1].title}
                </h3>
                <p className="pg-modal-sub">
                  {MCQ_STEP_COPY[mcqStep - 1].hint}
                </p>
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setShowAddForm(false)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {formNotice?.kind === "ok" ? (
                // Said in the dialog the work was done in; it closes itself
                // a few seconds later.
                <PopupDone
                  message={formNotice.text}
                  text="The course list behind this has been updated."
                  onClose={() => setShowAddForm(false)}
                />
              ) : (
              <Formik
                initialValues={mcqInitialValues}
                validationSchema={mcqValidationSchema}
                onSubmit={handleMcqSubmit}
              >
                {({
                  values,
                  errors,
                  touched,
                  handleChange,
                  handleBlur,
                  handleSubmit,
                  setFieldValue,
                  isSubmitting,
                }) => (
                  <form onSubmit={handleSubmit} className="mcq-form" noValidate>
                    {formNotice?.kind === "bad" && (
                      <p className="pg-notice pg-notice--bad" role="status">
                        {formNotice.text}
                      </p>
                    )}

                    <ol className="att-steps">
                      {MCQ_STEPS.map((label, i) => {
                        const n = i + 1;
                        const state =
                          n === mcqStep ? "is-now" : n < mcqStep ? "is-done" : "";
                        return (
                          <li className={`att-step-tab ${state}`.trim()} key={label}>
                            <button
                              type="button"
                              disabled={n >= mcqStep}
                              onClick={() => setMcqStep(n)}
                            >
                              <span className="att-step-n">{n}</span>
                              {label}
                            </button>
                          </li>
                        );
                      })}
                    </ol>

                    <div className={`att-stage is-${mcqPhase}`}>
                      <div className="att-stage-inner" key={mcqShownStep}>
                        {mcqShownStep === 1 && (
                          <section className="att-step">
                            <div className="pg-field">
                              <label className="pg-label" htmlFor="mcq-question">
                                Question
                              </label>
                              <textarea
                                id="mcq-question"
                                name="question"
                                className="pg-input mcq-textarea"
                                rows={4}
                                placeholder="Enter the question"
                                value={values.question}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              />
                              {touched.question && errors.question && (
                                <p className="att-error">{errors.question}</p>
                              )}
                            </div>
                          </section>
                        )}

                        {mcqShownStep === 2 && (
                          <section className="att-step">
                            <div className="mcq-options">
                              {[1, 2, 3, 4].map((n) => {
                                const key = `option${n}`;
                                return (
                                  <div className="pg-field" key={key}>
                                    <label
                                      className="pg-label"
                                      htmlFor={`mcq-${key}`}
                                    >
                                      Option {n}
                                    </label>
                                    <input
                                      id={`mcq-${key}`}
                                      name={key}
                                      type="text"
                                      className="pg-input"
                                      placeholder={`Enter option ${n}`}
                                      value={values[key]}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                    />
                                    {touched[key] && errors[key] && (
                                      <p className="att-error">{errors[key]}</p>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="pg-field mcq-answer">
                              <label className="pg-label" htmlFor="mcq-answer">
                                Correct answer
                              </label>
                              <select
                                id="mcq-answer"
                                name="correctAnswer"
                                className="pg-select"
                                value={values.correctAnswer}
                                onChange={handleChange}
                                onBlur={handleBlur}
                              >
                                <option value="">Select the correct option</option>
                                {[1, 2, 3, 4].map((n) => (
                                  <option key={n} value={n}>
                                    {values[`option${n}`]
                                      ? `Option ${n} — ${values[`option${n}`]}`
                                      : `Option ${n}`}
                                  </option>
                                ))}
                              </select>
                              {touched.correctAnswer && errors.correctAnswer && (
                                <p className="att-error">{errors.correctAnswer}</p>
                              )}
                            </div>
                          </section>
                        )}

                        {mcqShownStep === 3 && (
                          <section className="att-step">
                            <div className="mcq-options">
                              <div className="pg-field">
                                <label className="pg-label">Courses</label>
                                <NewCustomAsyncSelectDropdown
                                  options={formCourseOptions}
                                  value={values.selectedCourses || []}
                                  onChange={(sel) =>
                                    setFieldValue("selectedCourses", sel || [])
                                  }
                                  placeholder="Select courses..."
                                  isMulti={true}
                                  isLoading={formCourseLoading}
                                  searchInControl={true}
                                  onMenuOpen={async () => {
                                    setFormCoursePage(1);
                                    setFormCourseSearch("");
                                    await fetchFormCourses(1, "", false);
                                  }}
                                  onInputChange={debounce(async (value) => {
                                    setFormCourseSearch(value);
                                    setFormCoursePage(1);
                                    await fetchFormCourses(1, value, false);
                                  }, 400)}
                                  onMenuScrollToBottom={async () => {
                                    if (formCourseHasMore && !formCourseLoading) {
                                      await fetchFormCourses(
                                        formCoursePage + 1,
                                        formCourseSearch,
                                        true,
                                      );
                                    }
                                  }}
                                  onRefresh={async () => {
                                    setFormCoursePage(1);
                                    setFormCourseSearch("");
                                    await fetchFormCourses(1, "", false);
                                  }}
                                />
                                {touched.selectedCourses &&
                                  errors.selectedCourses && (
                                    <p className="att-error">
                                      {errors.selectedCourses}
                                    </p>
                                  )}
                              </div>

                              <div className="pg-field">
                                <label className="pg-label">Trainer</label>
                                {isTrainer ? (
                                  <input
                                    type="text"
                                    className="pg-input"
                                    value={getCurrentTrainerName()}
                                    readOnly
                                    disabled
                                  />
                                ) : (
                                  <NewCustomAsyncSelectDropdown
                                    options={filterTrainerOptions}
                                    value={
                                      values.selectedTrainer
                                        ? [values.selectedTrainer]
                                        : []
                                    }
                                    onChange={(opts) =>
                                      setFieldValue(
                                        "selectedTrainer",
                                        Array.isArray(opts) && opts.length
                                          ? opts[opts.length - 1]
                                          : null,
                                      )
                                    }
                                    placeholder="Select trainer..."
                                    isMulti={false}
                                    isLoading={filterTrainerLoading}
                                    searchInControl={true}
                                    onMenuOpen={async () => {
                                      setFilterTrainerPage(1);
                                      setFilterTrainerSearch("");
                                      await fetchFilterTrainers(1, "", false);
                                    }}
                                    onInputChange={debounce(async (value) => {
                                      setFilterTrainerSearch(value);
                                      setFilterTrainerPage(1);
                                      await fetchFilterTrainers(1, value, false);
                                    }, 400)}
                                    onMenuScrollToBottom={async () => {
                                      if (filterTrainerHasMore && !filterTrainerLoading) {
                                        await fetchFilterTrainers(
                                          filterTrainerPage + 1,
                                          filterTrainerSearch,
                                          true,
                                        );
                                      }
                                    }}
                                  />
                                )}
                                {touched.selectedTrainer &&
                                  errors.selectedTrainer && (
                                    <p className="att-error">
                                      {errors.selectedTrainer}
                                    </p>
                                  )}
                              </div>
                            </div>
                          </section>
                        )}
                      </div>
                    </div>

                    <div className="att-actions">
                      {mcqStep > 1 ? (
                        <button
                          type="button"
                          className="pg-btn"
                          onClick={() => setMcqStep(mcqStep - 1)}
                        >
                          Back
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="pg-btn"
                          onClick={() => setShowAddForm(false)}
                        >
                          Cancel
                        </button>
                      )}

                      {mcqStep < 3 ? (
                        <button
                          key="mcq-next"
                          type="button"
                          className="pg-btn pg-btn--main"
                          // Nothing moves on until this step is answered.
                          disabled={
                            !mcqStepComplete(mcqStep, values, errors, isAdmin)
                          }
                          onClick={() => setMcqStep(mcqStep + 1)}
                        >
                          Continue
                        </button>
                      ) : (
                        <button
                          key="mcq-save"
                          type="button"
                          onClick={() => handleSubmit()}
                          className="pg-btn pg-btn--main"
                          disabled={
                            isSubmitting ||
                            formSubmitting ||
                            !mcqStepComplete(1, values, errors, isAdmin) ||
                            !mcqStepComplete(2, values, errors, isAdmin) ||
                            !mcqStepComplete(3, values, errors, isAdmin)
                          }
                        >
                          {isSubmitting || formSubmitting ? (
                            <>
                              <span
                                className="pg-spinner att-spinner"
                                aria-hidden="true"
                              />
                              Adding...
                            </>
                          ) : (
                            "Add MCQ"
                          )}
                        </button>
                      )}
                    </div>
                  </form>
                )}
              </Formik>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DisplayMcqCourse;